import { Box } from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";

import { ColumnFilterOption } from "@/common/components/table/ColumnFilterHeader";
import {
  dataTableLocales,
  dataTableSortIconSlots,
  getDataTableChromeSx,
} from "@/common/components/table/DataTable.utils";
import { useLanguageContext } from "@/common/contexts/language-context/LanguageContext";

import { TakeoverHistoryRow } from "../TakeoverHistoryPanel.types";
import { useTakeoverHistoryColumns } from "../hooks/useTakeoverHistoryColumns";

interface Props {
  rows: TakeoverHistoryRow[];
  isLoading: boolean;
  serviceOptions: ColumnFilterOption[];
  serviceFilter: string | null;
  onServiceFilterChange: (value: string | null) => void;
}

/** Flat, read-only table of past takeovers, newest first, with a gray header and striped rows. */
export function TakeoverHistoryTable({ rows, isLoading, serviceOptions, serviceFilter, onServiceFilterChange }: Props) {
  const { language } = useLanguageContext();
  const columns = useTakeoverHistoryColumns({ serviceOptions, serviceFilter, onServiceFilterChange });

  return (
    <Box sx={{ height: "100%", width: "100%" }}>
      <DataGrid
        rows={rows}
        columns={columns}
        // An appointment can be taken over more than once, so the appointment id alone is not unique;
        // combine it with the takeover instant for a stable row id.
        getRowId={(row) => `${row.appointmentId}-${new Date(row.takeoverTimestampUtc).getTime()}`}
        loading={isLoading}
        rowHeight={60}
        columnHeaderHeight={56}
        initialState={{ sorting: { sortModel: [{ field: "takeoverTimestampUtc", sort: "desc" }] } }}
        disableRowSelectionOnClick
        disableColumnSelector
        disableColumnMenu
        disableColumnResize
        hideFooter
        getRowClassName={(params) => (params.indexRelativeToCurrentPage % 2 === 0 ? "even-row" : "odd-row")}
        slots={dataTableSortIconSlots}
        localeText={dataTableLocales[language]}
        sx={(theme) => getDataTableChromeSx(theme)}
      />
    </Box>
  );
}
